"use client"
import { useTheme } from "@mui/material/styles"
import { FC, memo } from "react"

import { Avatar, AvatarProps } from "../avatar"
import { Headline, HeadlineProps } from "../headline"
import { RichText } from "../rich-text"

import styles from "./Quote.module.scss"

import { create } from "@/helpers/bem"
import { Variants } from "@/types/variants"

const bem = create(styles, "Quote")

export type QuoteHeadline = {
  content: string
} & Omit<HeadlineProps, "children">

export type QuoteAuthor = {
  avatar?: AvatarProps
  name: string
  position?: string
}

export type QuoteProps = {
  className?: string
  variant?: Variants
  title?: QuoteHeadline
  subTitle?: QuoteHeadline
  content: string
  author?: QuoteAuthor
}

export const Quote: FC<QuoteProps> = memo(
  ({ className, variant = "primary", title, subTitle, content, author }) => {
    const theme = useTheme()
    const themeModifier = {
      [theme.palette.mode]: true,
    }
    return (
      <figure
        itemScope
        className={bem(undefined, undefined, className)}
        itemType="https://schema.org/Quotation"
      >
        {subTitle && (
          <Headline
            highlight
            className={bem("sub_headline", undefined, subTitle?.className)}
            size="sm"
            type="h3"
            {...subTitle}
            variant={subTitle.variant ?? variant}
          >
            {subTitle?.content}
          </Headline>
        )}
        {title && (
          <Headline
            className={bem("headline", undefined, title?.className)}
            size="lg"
            type="h2"
            {...title}
          >
            {title.content}
          </Headline>
        )}
        <blockquote className={bem("quote")}>
          <RichText className={bem("quote__content")} itemProp="text">
            {content}
          </RichText>
        </blockquote>
        {author && (
          <figcaption>
            <div
              itemScope
              className={bem("caption")}
              itemType="https://schema.org/Person"
            >
              {author?.avatar && (
                <Avatar
                  className={bem("caption__avatar")}
                  {...author.avatar}
                  variant={variant}
                />
              )}
              <div className={bem("caption__author", themeModifier)}>
                <span className={bem("caption__author__name")} itemProp="name">
                  {author.name}
                </span>
                {author?.position && (
                  <span
                    className={bem("caption__author__position")}
                    itemProp="jobTitle"
                  >
                    {author.position}
                  </span>
                )}
              </div>
            </div>
          </figcaption>
        )}
      </figure>
    )
  },
)

Quote.displayName = "Quote"
